import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtom } from 'jotai';
import { Card, Group, ActionIcon, Text, Button, Loader, Tooltip } from '@mantine/core';
import { IconArrowLeft, IconShare3, IconSettings } from '@tabler/icons-react';
import { messageStyleAtom } from '@/stores/atoms/styleAtoms';
import { chatOverlayOpenAtom, chatOverlayReturnAtom, activePageAtom, localChatModeAtom } from '@/stores/atoms/uiAtoms';
import { currentChatAtom } from '@/stores/atoms/chatAtoms';
import ChatWindow from './ChatWindow';
import { resolveServerUrl, getApiBase } from '@/renderer/utils/session';
import { httpFetch } from '@/renderer/utils/http';
import { useSwipe } from '@/renderer/utils/gestures';
import { useT } from '@/i18n';
import { wsManager } from '@/utils/wsManager';
import { useAtomValue } from 'jotai';
import { sessionsAtom, currentSessionIdAtom, updateSessionAtom } from '@/stores/atoms/sessionAtoms';
import { sendMessageAtom } from '@/stores/atoms/chatAtoms';
import { languageAtom } from '@/stores/atoms/i18nAtoms';
import { currentScriptIdAtom } from '@/stores/atoms/scriptAtoms';
import { fetchModels } from '@/utils/gameApi';
import { getAppBaseUrl, getOrCreateUserId } from '@/utils/session';
import { notifications } from '@mantine/notifications';
import { authUserAtom, authReadyAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { getStoredAuthToken } from '@/utils/userApi';
import { useIsDesktop } from '@/renderer/utils/responsive';
import { novelChatSettingsAtom } from '@/stores/atoms/novelChatSettingsAtoms';
import NovelSettingsPanel from './NovelSettingsPanel';
import { readingConfigAtom } from '@/stores/atoms/readingConfigAtoms';
import DiamondsIndicator from '@/components/common/DiamondsIndicator';
const ChatOverlay = () => {
    const t = useT();
    const [openAtom, setOpenAtom] = useAtom(chatOverlayOpenAtom);
    const [ret] = useAtom(chatOverlayReturnAtom);
    const [, setActivePage] = useAtom(activePageAtom);
    const [currentChat] = useAtom(currentChatAtom);
    const [, setLocalMode] = useAtom(localChatModeAtom);
    const [visible, setVisible] = React.useState(false);
    const [phase, setPhase] = React.useState('idle');
    const [msgStyle, setMsgStyle] = useAtom(messageStyleAtom);
    const [wsStatus, setWsStatus] = React.useState({ state: 'open', attempts: 0 });
    const isDesktop = useIsDesktop(1024);
    React.useEffect(() => {
        // 小说对话默认使用“小说纸页”风格
        if (msgStyle !== 'novel')
            setMsgStyle('novel');
    }, [msgStyle, setMsgStyle]);
    // Subscribe to WS status for auto-reconnect feedback
    React.useEffect(() => {
        const unsub = wsManager.onStatus((s) => setWsStatus(s));
        return () => { try {
            unsub?.();
        }
        catch { } };
    }, []);
    // Do not auto-close WS on overlay visibility changes; only close on explicit back or app exit
    // On open: if it's a brand new session (no serverSessionId), connect immediately
    const sessions = useAtomValue(sessionsAtom);
    const currentSessionId = useAtomValue(currentSessionIdAtom);
    const [globalLang] = useAtom(languageAtom);
    const [activeScriptId] = useAtom(currentScriptIdAtom);
    const [, sendChatMessage] = useAtom(sendMessageAtom);
    const authUser = useAtomValue(authUserAtom);
    const authReady = useAtomValue(authReadyAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    const [novelSettings, setNovelSettings] = useAtom(novelChatSettingsAtom);
    const [, setReadingCfg] = useAtom(readingConfigAtom);
    const chatLang = novelSettings?.language || globalLang;
    // Models for this session
    const [models, setModels] = React.useState([]);
    const [modelCode, setModelCode] = React.useState(undefined);
    // Deduplicate and sanitize model options early so later effects can depend on it safely
    const modelOptions = React.useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const m of models || []) {
            const code = (m?.model || '').trim();
            if (!code || seen.has(code))
                continue;
            seen.add(code);
            out.push({ name: m.name, model: code });
        }
        return out;
    }, [models]);
    const [, updateSession] = useAtom(updateSessionAtom);
    const connectAttemptedRef = React.useRef(false);
    React.useEffect(() => {
        if (!openAtom)
            connectAttemptedRef.current = false;
    }, [openAtom]);
    React.useEffect(() => {
        if (!openAtom)
            return;
        if (connectAttemptedRef.current)
            return;
        const cur = currentSessionId ? sessions.find((s) => s.id === currentSessionId) : undefined;
        const serverSessionId = cur?.serverSessionId;
        // wait until model selection ready (or no models available)
        const modelReady = (modelOptions && modelOptions.length ? !!modelCode : true);
        const hasToken = !!getStoredAuthToken();
        const canConnect = !!authUser && !!authReady && hasToken;
        if (openAtom && !canConnect) {
            try {
                setAuthModalOpen(true);
            }
            catch { }
        }
        if (activeScriptId && modelReady && canConnect) {
            try {
                // Always close any previous socket before connecting to guarantee a single connection
                try {
                    wsManager.close();
                }
                catch { }
                wsManager.connect({ scriptId: activeScriptId, language: chatLang, sessionId: serverSessionId, modelCode: modelCode });
                connectAttemptedRef.current = true;
            }
            catch { }
        }
    }, [openAtom, currentSessionId, sessions, chatLang, activeScriptId, modelOptions.length, modelCode, authUser, authReady, setAuthModalOpen]);
    // Debug info to verify UI overlay conditions
    const curSession = React.useMemo(() => (currentSessionId ? sessions.find((s) => s.id === currentSessionId) : null), [sessions, currentSessionId]);
    React.useEffect(() => {
        if (!openAtom)
            return;
        try {
            // eslint-disable-next-line no-console
            console.log('[ChatOverlay] open=', openAtom, 'sessionId=', currentSessionId, 'hasUiCode=', !!(curSession && curSession.scriptUiCode && curSession.scriptUiCode.trim().length));
        }
        catch { }
    }, [openAtom, currentSessionId, sessions, curSession]);
    React.useEffect(() => {
        if (openAtom) {
            setVisible(true);
            requestAnimationFrame(() => setPhase('opening'));
            const t = window.setTimeout(() => setPhase('open'), 260);
            return () => clearTimeout(t);
        }
        if (!openAtom && visible) {
            setPhase('closing');
        }
    }, [openAtom]);
    const onTransitionEnd = () => {
        if (phase === 'closing') {
            setVisible(false);
            setPhase('idle');
        }
    };
    const goBack = () => {
        try {
            wsManager.close();
        }
        catch { }
        setOpenAtom(false);
        const t = window.setTimeout(() => setActivePage(ret), 260);
        setLocalMode(false);
        return () => clearTimeout(t);
    };
    const swipeToClose = useSwipe({ threshold: 60, onRight: goBack, startEdgeLeftPx: 24 });
    // Ensure WS is closed whenever overlay exits (no auto-reconnect after exit)
    React.useEffect(() => {
        if (!openAtom) {
            try {
                wsManager.close();
            }
            catch { }
        }
    }, [openAtom]);
    // HTML pager overlay integration (full-cover except input area)
    const [inputBottomPx, setInputBottomPx] = React.useState(0);
    // Load models once and restore selection per-session
    React.useEffect(() => {
        if (!openAtom)
            return;
        (async () => {
            try {
                const uid = getOrCreateUserId();
                const list = await fetchModels(uid, chatLang);
                setModels((list || []).filter((m) => !!(m && typeof m.name === 'string' && m.name.trim().length > 0)));
            }
            catch { }
        })();
    }, [openAtom, chatLang]);
    // Fetch reading config (language/style/pov) when entering conversation page
    React.useEffect(() => {
        if (!openAtom)
            return;
        (async () => {
            try {
                const base = getApiBase();
                const url = `${base}/api/reading/config`;
                try {
                    console.log('[ReadingConfig] fetch start', { url });
                }
                catch { }
                const res = await httpFetch(url, { timeoutMs: 10000 });
                try {
                    console.log('[ReadingConfig] response', { ok: res.ok, status: res.status });
                }
                catch { }
                let data = undefined;
                try {
                    data = await res.json();
                }
                catch { }
                try {
                    console.log('[ReadingConfig] raw payload keys', data ? Object.keys(data || {}) : 'no-data');
                    if (data) {
                        const ln = Array.isArray(data.languages || data.language_options) ? (data.languages || data.language_options).length : 0;
                        const st = Array.isArray(data.styles || data.writing_styles || data.style_options) ? (data.styles || data.writing_styles || data.style_options).length : 0;
                        const pn = Array.isArray(data.persons || data.person_options) ? (data.persons || data.person_options).length : 0;
                        console.log('[ReadingConfig] raw sizes', { languages: ln, styles: st, persons: pn, defaults: data.defaults || {} });
                    }
                }
                catch { }
                // Normalize server config to local options
                const normList = (arr, valueKey = 'value', labelKey = 'label') => {
                    if (!Array.isArray(arr))
                        return [];
                    const out = [];
                    for (const it of arr) {
                        if (it == null)
                            continue;
                        if (typeof it === 'string') {
                            out.push({ value: it, label: it });
                            continue;
                        }
                        const v = String(it[valueKey] ?? it.code ?? it.id ?? it.value ?? '').trim();
                        if (!v)
                            continue;
                        const l = String(it[labelKey] ?? it.name ?? it.label ?? v);
                        out.push({ value: v, label: l });
                    }
                    // dedupe by value
                    const seen = new Set();
                    return out.filter((x) => (x.value && !seen.has(x.value) && (seen.add(x.value), true)));
                };
                // Support category-shaped payload: { categories: [ { id, name, options: [{ id, name }] } ] }
                const categories = Array.isArray(data?.categories) ? data.categories : [];
                const pickCat = (ids) => {
                    const lower = ids.map((s) => String(s || '').toLowerCase());
                    for (const c of categories) {
                        const cid = String(c?.id || '').toLowerCase();
                        if (lower.includes(cid))
                            return c;
                    }
                    return null;
                };
                const catLang = pickCat(['language', 'lang', 'languages']);
                const catStyle = pickCat(['style', 'writing_style', 'styles']);
                const catPov = pickCat(['pov', 'person', 'person_view', 'point_of_view']);
                if (categories.length) {
                    try {
                        console.log('[ReadingConfig] categories detected', {
                            count: categories.length,
                            hasLang: !!catLang,
                            hasStyle: !!catStyle,
                            hasPov: !!catPov,
                            langOpts: Array.isArray(catLang?.options) ? catLang.options.length : 0,
                            styleOpts: Array.isArray(catStyle?.options) ? catStyle.options.length : 0,
                            povOpts: Array.isArray(catPov?.options) ? catPov.options.length : 0,
                        });
                    }
                    catch { }
                }
                const cfg = {
                    languages: (() => {
                        const direct = normList(data?.languages || data?.language_options);
                        if (direct.length)
                            return direct;
                        return normList(catLang?.options, 'id', 'name');
                    })(),
                    styles: (() => {
                        const direct = normList(data?.styles || data?.writing_styles || data?.style_options, 'id', 'name');
                        if (direct.length)
                            return direct;
                        return normList(catStyle?.options, 'id', 'name');
                    })(),
                    persons: (() => {
                        const direct = normList(data?.persons || data?.person_options);
                        if (direct.length)
                            return direct;
                        return normList(catPov?.options, 'id', 'name');
                    })(),
                    defaults: {
                        language: String(data?.defaults?.language || data?.default_language || ''),
                        writingStyle: String(data?.defaults?.writing_style || data?.default_style || ''),
                        person: String(data?.defaults?.person || data?.default_person || ''),
                        fontSize: Number.isFinite(Number(data?.defaults?.font_size)) ? Number(data?.defaults?.font_size) : undefined,
                    },
                };
                try {
                    console.log('[ReadingConfig] normalized', {
                        languages: cfg.languages,
                        styles: cfg.styles,
                        persons: cfg.persons,
                        defaults: cfg.defaults,
                    });
                }
                catch { }
                // Apply fallbacks if server missing fields
                if (!cfg.languages.length)
                    cfg.languages = [{ value: 'zh-CN', label: '中文' }, { value: 'en-US', label: 'English' }];
                if (!cfg.styles.length)
                    cfg.styles = [
                        { value: 'plain', label: '简洁' },
                        { value: 'immersive', label: '沉浸' },
                        { value: 'humor', label: '幽默' },
                        { value: 'classical', label: '古风' },
                        { value: 'scifi', label: '科幻' },
                    ];
                if (!cfg.persons.length)
                    cfg.persons = [
                        { value: 'first', label: '第一人称' },
                        { value: 'second', label: '第二人称' },
                        { value: 'third', label: '第三人称' },
                    ];
                setReadingCfg(cfg);
                try {
                    console.log('[ReadingConfig] setReadingCfg applied');
                }
                catch { }
                // Validate and apply defaults to current settings if invalid
                const valid = (val, list) => !!(val && list.some((x) => x.value === val));
                const patch = {};
                if (!valid(novelSettings.language, cfg.languages)) {
                    patch.language = cfg.defaults?.language || (cfg.languages[0]?.value || 'zh-CN');
                }
                if (!valid(novelSettings.writingStyle, cfg.styles)) {
                    patch.writingStyle = cfg.defaults?.writingStyle || (cfg.styles[0]?.value || 'immersive');
                }
                if (!valid(novelSettings.person, cfg.persons)) {
                    patch.person = cfg.defaults?.person || (cfg.persons[0]?.value || 'second');
                }
                if (cfg.defaults?.fontSize && !Number.isNaN(cfg.defaults.fontSize)) {
                    if (!novelSettings.fontSize || novelSettings.fontSize <= 0)
                        patch.fontSize = cfg.defaults.fontSize;
                }
                if (Object.keys(patch).length) {
                    try {
                        console.log('[ReadingConfig] patch settings', { before: novelSettings, patch });
                    }
                    catch { }
                    setNovelSettings({ ...novelSettings, ...patch });
                }
                else {
                    try {
                        console.log('[ReadingConfig] no patch needed, settings valid', novelSettings);
                    }
                    catch { }
                }
            }
            catch (e) {
                console.warn('[ReadingConfig] fetch error', e?.message || e);
            }
        })();
    }, [openAtom]);
    React.useEffect(() => {
        if (!openAtom)
            return;
        // derive selection from session or default to first in list
        const saved = curSession?.selectedModelCode;
        if (saved) {
            setModelCode(saved);
            try {
                wsManager.setModelCode(saved);
            }
            catch { }
        }
        else if (modelOptions && modelOptions.length) {
            const first = modelOptions[0].name;
            setModelCode(first);
            try {
                wsManager.setModelCode(first);
            }
            catch { }
            // persist to session
            if (curSession) {
                updateSession({ ...curSession, selectedModelCode: first });
            }
            try {
                setNovelSettings({ ...novelSettings, modelCode: first });
            }
            catch { }
        }
    }, [openAtom, curSession?.id, modelOptions.length]);
    // When user changes model in NovelSettingsPanel, apply it to wsManager and persist to session
    React.useEffect(() => {
        if (!openAtom)
            return;
        const selected = (novelSettings?.modelCode || '').trim();
        if (!selected)
            return;
        if (modelCode !== selected) {
            setModelCode(selected);
            try {
                wsManager.setModelCode(selected);
            }
            catch { }
        }
        if (currentSessionId) {
            const sess = sessions.find((s) => s.id === currentSessionId);
            if (sess && sess.selectedModelCode !== selected) {
                updateSession({ ...sess, selectedModelCode: selected });
            }
        }
    }, [openAtom, novelSettings?.modelCode, currentSessionId, sessions]);
    // Disable UI overlay: ignore incoming UI pages/frames to avoid covering chat
    const onUiData = undefined;
    const [showSettings, setShowSettings] = React.useState(false);
    if (!visible)
        return null;
    return (_jsx("div", { className: `chat-overlay ${isDesktop ? 'chat-overlay-desktop' : ''}`, children: _jsxs("div", { className: `chat-overlay-panel ${phase}`, onTransitionEnd: onTransitionEnd, ...swipeToClose, style: (() => {
                const url = resolveServerUrl(curSession?.scriptImageUrl || '');
                const base = {};
                // Apply novel font size CSS variable for novel page style rendering
                const fs = (novelSettings?.fontSize ?? 16);
                base['--novel-font-size'] = `${fs}px`;
                if (!url)
                    return base;
                return {
                    ...base,
                    backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url("${url}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                };
            })(), children: [_jsx("div", { className: "overlay-top-gap", "aria-hidden": true }), _jsx(Card, { withBorder: true, radius: 0, p: "sm", className: "overlay-header no-safe-top", children: _jsxs(Group, { justify: "space-between", gap: "sm", wrap: "nowrap", children: [_jsxs(Group, { gap: "sm", wrap: "nowrap", style: { minWidth: 0 }, children: [_jsx(ActionIcon, { variant: "light", radius: "xl", onClick: goBack, "aria-label": t('back'), children: _jsx(IconArrowLeft, { size: 18 }) }), _jsx("div", { style: { maxWidth: 'min(55vw, 320px)', overflow: 'hidden' }, children: _jsx(Text, { fw: 800, size: "sm", style: { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }, children: currentChat?.title || 'Chat' }) })] }), _jsxs(Group, { gap: "xs", wrap: "nowrap", children: [_jsx(DiamondsIndicator, { compact: true }), _jsx(Tooltip, { label: t('settings.title') || '设置', children: _jsx(ActionIcon, { variant: "subtle", radius: "xl", "aria-label": t('settings.title') || '设置', onClick: () => setShowSettings(true), children: _jsx(IconSettings, { size: 18 }) }) }), _jsx(Tooltip, { label: t('share') || '分享', children: _jsx(ActionIcon, { variant: "subtle", radius: "xl", "aria-label": t('share') || '分享', onClick: async () => {
                                                try {
                                                    const base = getAppBaseUrl();
                                                    if (!activeScriptId)
                                                        throw new Error('No script selected');
                                                    const link = `${base}/?script=${encodeURIComponent(activeScriptId)}`;
                                                    try {
                                                        await navigator.clipboard.writeText(link);
                                                    }
                                                    catch { }
                                                    notifications.show({ color: 'green', title: '分享链接已生成', message: '已复制到剪贴板，粘贴发送给好友', withCloseButton: true });
                                                }
                                                catch (e) {
                                                    notifications.show({ color: 'red', title: '生成分享链接失败', message: String(e?.message || 'Server error'), withCloseButton: true });
                                                }
                                            }, children: _jsx(IconShare3, { size: 18 }) }) })] })] }) }), wsStatus.state !== 'open' && (_jsx(Card, { withBorder: true, radius: 0, p: "xs", style: { background: 'rgba(255, 200, 0, 0.06)' }, children: _jsxs(Group, { justify: "space-between", gap: "sm", wrap: "nowrap", children: [_jsxs(Group, { gap: 8, wrap: "nowrap", children: [(wsStatus.state === 'connecting' || wsStatus.state === 'reconnecting') && _jsx(Loader, { size: "xs" }), _jsx(Text, { size: "sm", c: "dimmed", children: wsStatus.state === 'connecting'
                                            ? t('ws.connecting')
                                            : wsStatus.state === 'reconnecting'
                                                ? t('ws.reconnecting').replace('{n}', String(wsStatus.attempts))
                                                : t('ws.disconnected') })] }), _jsx(Button, { size: "xs", variant: "light", onClick: () => wsManager.retryNow(), children: t('ws.retry') })] }) })), _jsx("div", { style: { flex: 1, minHeight: 0, position: 'relative' }, children: showSettings ? (_jsx(NovelSettingsPanel, { onClose: () => setShowSettings(false) })) : (_jsx(ChatWindow, { messageStyle: msgStyle, onFloatsHeightChange: setInputBottomPx })) })] }) }));
};
export default ChatOverlay;
